import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import './PatchBurst.css';

export const PATCH_BURST_MOTIONS = ['pop-flick', 'swing-shrink', 'lift-vanish'];

/** One patch's run (enter → settle → hover → exit), ms — handed to the CSS as --pb-dur */
export const PATCH_BURST_ITEM_MS = 428;
/** Longest we wait for the PNGs before starting anyway, ms */
const PRELOAD_CAP_MS = 400;

const preload = (srcs) =>
  Promise.all(
    srcs.map(
      (src) =>
        new Promise((resolve) => {
          const img = new Image();
          img.onload = () => (img.decode ? img.decode().then(resolve, resolve) : resolve());
          img.onerror = resolve;
          img.src = src;
        }),
    ),
  );

/**
 * PatchBurst — a short, authored "patches popping around the text" scene:
 * each patch MATERIALISES at its own point the way BlurReveal's glyphs do
 * (same DS blur, lift and curve), swells with a little elasticity, tilts,
 * hangs for a beat and dematerialises away. Pure decoration, played ONCE on
 * mount (the layer unmounts when the last patch is gone), hidden from
 * assistive tech, no pointer events, no layout impact.
 *
 * Used for the OVER/LL hero (HeroBannerV3 `burst`), which schedules the scene
 * backwards from the text reveal's end so both finish together. Everything is configuration (`items`): position in % of
 * the host box, size, final tilt, start delay and one of three motions —
 * `pop-flick` (elastic pop, quick flick sideways), `swing-shrink` (comes in
 * turning, short sway, shrinks away), `lift-vanish` (rises in on an arc,
 * hangs, lifts out). Composition is deliberate, never random.
 *
 * Mobile: an item's `mobile` is its own `{x, y, size}` or `false` to skip it.
 * transform + opacity only (a 6px blur — ½ of the M2 blur token — on the enter beat); honours
 * prefers-reduced-motion (nothing renders).
 */
const PatchBurst = ({ items = [], delay = 0, onDone, className = '' }) => {
  const [ready, setReady] = useState(false);
  const [done, setDone] = useState(false);

  const srcs = useMemo(() => items.map((i) => i.image), [items]);
  const sceneMs = useMemo(
    () => (items.length ? Math.max(...items.map((i) => i.delay || 0)) + PATCH_BURST_ITEM_MS : 0),
    [items],
  );

  // Start once the PNGs are decoded (capped), so no patch pops in late
  useEffect(() => {
    if (!srcs.length) return undefined;
    let alive = true;
    const start = () => alive && setReady(true);
    const cap = setTimeout(start, PRELOAD_CAP_MS);
    preload(srcs).then(start);
    return () => {
      alive = false;
      clearTimeout(cap);
    };
  }, [srcs]);

  // Drop the layer when the scene has played
  useEffect(() => {
    if (!ready) return undefined;
    const t = setTimeout(() => {
      setDone(true);
      onDone?.();
    }, delay + sceneMs + 100);
    return () => clearTimeout(t);
  }, [ready, delay, sceneMs, onDone]);

  if (!items.length || !ready || done) return null;

  return (
    <div
      className={`patch-burst${className ? ` ${className}` : ''}`}
      style={{ '--pb-dur': `${PATCH_BURST_ITEM_MS}ms` }}
      aria-hidden="true"
    >
      {items.map((item, i) => {
        const vars = {
          '--pb-x': `${item.x}%`,
          '--pb-y': `${item.y}%`,
          '--pb-size': `${item.size}px`,
          '--pb-rotate': `${item.rotate || 0}deg`,
          '--pb-delay': `${delay + (item.delay || 0)}ms`,
        };
        if (item.mobile) {
          vars['--pb-x-m'] = `${item.mobile.x}%`;
          vars['--pb-y-m'] = `${item.mobile.y}%`;
          vars['--pb-size-m'] = `${item.mobile.size}px`;
        }
        const cls = [
          'patch-burst__item',
          `patch-burst__item--${item.motion || 'pop-flick'}`,
          item.mobile === false ? 'patch-burst__item--desktop' : '',
        ].filter(Boolean).join(' ');
        return (
          <div key={item.id || i} className={cls} style={vars}>
            <img className="patch-burst__img" src={item.image} alt="" draggable="false" />
          </div>
        );
      })}
    </div>
  );
};

PatchBurst.propTypes = {
  /** The scene: [{ id, image, x, y, size, rotate, delay, motion, mobile }] — x/y in % of the host, size = width px, rotate = final tilt deg, delay ms from scene start, mobile = {x, y, size} | false */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      image: PropTypes.string.isRequired,
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
      size: PropTypes.number.isRequired,
      rotate: PropTypes.number,
      delay: PropTypes.number,
      motion: PropTypes.oneOf(PATCH_BURST_MOTIONS),
      mobile: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.shape({ x: PropTypes.number, y: PropTypes.number, size: PropTypes.number }),
      ]),
    }),
  ),
  /** Offset for the whole scene, ms (align with the text reveal) */
  delay: PropTypes.number,
  /** Called once the layer has unmounted after the last patch */
  onDone: PropTypes.func,
  /** Additional CSS class on the layer */
  className: PropTypes.string,
};

export default PatchBurst;
